/**
 * Raw design tokens — the primitive layer (mode-agnostic literals). Semantic tokens
 * (see `semantic-tokens.ts`) reference and remap these per color mode. Feature code
 * must NOT read raw tokens directly; it consumes semantic tokens via primitives.
 *
 * Everything is a Chakra v3 token: `{ value: <literal> }`.
 */
import { fontFamilies } from './fonts';

const t = <T extends string | number>(value: T) => ({ value });

export const tokens = {
  /* ---- color primitives -------------------------------------------------- */
  colors: {
    /** Brand — EHE pink. The signature; used with intent, dark ink on pink, never
     *  white on pink, never as a status color. `500` is the toolkit anchor #f09ad7. */
    brand: {
      50: t('#fdf1fa'),
      100: t('#fbe3f4'),
      200: t('#f7cdec'),
      300: t('#f4b6e3'),
      400: t('#f2a5dc'),
      500: t('#f09ad7'), // anchor
      600: t('#d873ba'),
      700: t('#b04f95'),
      800: t('#8f2c71'),
      900: t('#5f1f4d'),
    },
    /** Action — EHE blue. The single interactive color. `500` is #005ff3. */
    blue: {
      50: t('#e9f1ff'),
      100: t('#cfe0ff'),
      200: t('#a3c3ff'),
      300: t('#6fa0ff'),
      400: t('#3b82ff'), // dark-mode action
      500: t('#005ff3'), // light-mode action
      600: t('#0050d1'),
      700: t('#0042ad'),
      800: t('#003788'),
      900: t('#062a63'),
    },
    /** Neutral — ink → paper ramp; near-black anchored at #141414. */
    ink: {
      0: t('#ffffff'),
      25: t('#fbfcfe'),
      50: t('#f4f6fb'),
      100: t('#eef2fb'),
      150: t('#e5eeff'), // EHE pale-blue wash
      200: t('#d5dae6'),
      300: t('#b3bacb'),
      400: t('#8b91a3'),
      500: t('#5f6472'),
      600: t('#45464f'),
      700: t('#2c2d34'),
      750: t('#202027'),
      800: t('#17171c'),
      900: t('#0e0e11'),
      950: t('#141414'),
    },
    /* status source colors — light-tuned and dark-tuned pairs */
    green: { light: t('#0a7a52'), dark: t('#43c58e'), subtleBg: t('#e2f4ec'), subtleBgDark: t('#123328'), onSubtle: t('#075c3e'), onSubtleDark: t('#5fd3a0') },
    amber: { light: t('#8a5a12'), dark: t('#e0a94a'), subtleBg: t('#f8ecd2'), subtleBgDark: t('#33260f'), onSubtle: t('#6f480e'), onSubtleDark: t('#e8b968') },
    red: { light: t('#b3160f'), dark: t('#f28b82'), subtleBg: t('#fbe4e2'), subtleBgDark: t('#3a1512'), onSubtle: t('#8f120c'), onSubtleDark: t('#f5a49c') },
    // `info` — in-flight/live work (Running, Queued, Waiting). Dark tuned onto the brand's
    // blue family (calm periwinkle, AA 7.5:1 on the navy subtle) instead of a neon cyan.
    cyan: { light: t('#0369a1'), dark: t('#6f9fe8'), subtleBg: t('#dcecf8'), subtleBgDark: t('#122840'), onSubtle: t('#0b5480'), onSubtleDark: t('#8fb9ff') },
  },

  /* ---- typography -------------------------------------------------------- */
  fonts: {
    heading: t(fontFamilies.heading),
    body: t(fontFamilies.body),
    mono: t(fontFamilies.mono),
  },
  fontSizes: {
    '2xs': t('0.6875rem'), // 11
    xs: t('0.75rem'), // 12
    sm: t('0.8125rem'), // 13
    md: t('0.875rem'), // 14 — base UI/body
    lg: t('1rem'), // 16
    xl: t('1.125rem'), // 18
    '2xl': t('1.375rem'), // 22
    '3xl': t('1.75rem'), // 28
    '4xl': t('2.25rem'), // 36
    '5xl': t('2.875rem'), // 46
    '6xl': t('3.75rem'), // 60 — display
  },
  fontWeights: {
    regular: t(400),
    medium: t(500),
    semibold: t(600),
    bold: t(700),
    extrabold: t(800),
  },
  lineHeights: {
    none: t(1),
    tight: t(1.12),
    snug: t(1.25),
    normal: t(1.5),
    relaxed: t(1.7),
  },
  letterSpacings: {
    tighter: t('-0.02em'),
    tight: t('-0.01em'),
    normal: t('0em'),
    wide: t('0.02em'),
    wider: t('0.08em'), // overlines / eyebrow labels
  },

  /* ---- shape ------------------------------------------------------------- */
  radii: {
    none: t('0'),
    xs: t('0.25rem'), // 4
    sm: t('0.375rem'), // 6
    md: t('0.5rem'), // 8 — default control radius
    lg: t('0.75rem'), // 12
    xl: t('1rem'), // 16
    '2xl': t('1.5rem'), // 24
    full: t('9999px'),
  },

  /* ---- content widths ---------------------------------------------------- */
  sizes: {
    'content.narrow': t('40rem'), // 640
    'content.default': t('67.5rem'), // 1080
    'content.wide': t('87.5rem'), // 1400
    prose: t('68ch'),
  },

  /* ---- motion ------------------------------------------------------------ */
  durations: {
    instant: t('0ms'),
    fast: t('120ms'),
    normal: t('200ms'),
    slow: t('320ms'),
  },
  easings: {
    standard: t('cubic-bezier(0.2, 0, 0, 1)'),
    emphasized: t('cubic-bezier(0.3, 0, 0, 1)'),
    exit: t('cubic-bezier(0.4, 0, 1, 1)'),
  },
} as const;

/** Breakpoints are a top-level Chakra theme key (plain values, not `{value}` tokens). */
export const breakpoints = {
  sm: '30rem', // 480
  md: '48rem', // 768
  lg: '64rem', // 1024
  xl: '80rem', // 1280
  '2xl': '96rem', // 1536
} as const;
